// NOTAMs tab — sub-tabs Airport / Obstacle / TFR-ARTCC.
//
// All three V1-stubbed. The FAA NOTAM API is free but requires a
// registered client_id + client_secret pair (api.faa.gov/portal). When
// we get those provisioned, /api/notams will land and these panels
// wire up. TFR sub-tab can lean on the existing /api/tfr endpoint
// already in production once we filter to ARTCC-relevant entries near
// the airport.

import { useState } from 'react';

type NotamKey = 'airport' | 'obstacle' | 'tfr';

const SUBTABS: Array<{ key: NotamKey; label: string }> = [
  { key: 'airport', label: 'Airport' },
  { key: 'obstacle', label: 'Obstacle' },
  { key: 'tfr', label: 'TFR / ARTCC' },
];

export function NotamTab({ ident }: { ident: string }) {
  const [sub, setSub] = useState<NotamKey>('airport');
  return (
    <div>
      <nav role="tablist" className="flex border-b border-cockpit-700 sticky top-0 bg-cockpit-800/95 backdrop-blur z-10">
        {SUBTABS.map((s) => (
          <button
            key={s.key}
            role="tab"
            aria-selected={sub === s.key}
            onClick={() => setSub(s.key)}
            className={[
              'flex-1 text-[11px] py-2 px-2 transition-colors whitespace-nowrap',
              sub === s.key
                ? 'text-ember-400 border-b-2 border-ember-500'
                : 'text-cockpit-300 hover:text-cockpit-100 border-b-2 border-transparent',
            ].join(' ')}
          >
            {s.label}
          </button>
        ))}
      </nav>
      <div className="px-4 py-3 text-sm">
        {sub === 'airport' && (
          <Stub
            label="Airport NOTAMs"
            source="FAA NOTAM API · external-api.faa.gov/notamapi · OAuth client_credentials"
            body={`Field-specific NOTAMs for ${ident} (runways closed, ILS unusable,
                   construction, lighting outages, etc.) will appear here once we register
                   for the FAA NOTAM API and provision the client_id/secret pair.`}
          />
        )}
        {sub === 'obstacle' && (
          <Stub
            label="Obstacle NOTAMs"
            source="FAA Daily Digital Obstacle File + NOTAM API class O"
            body="Towers, cranes, antennas, and temporary obstructions within ~10 nm of the field
                  show here. Class O NOTAMs are the canonical source; the daily DOF cross-references
                  the permanent set."
          />
        )}
        {sub === 'tfr' && (
          <Stub
            label="TFRs · ARTCC NOTAMs"
            source="FAA tfr.faa.gov + ARTCC NOTAMs"
            body={`Active TFRs near ${ident} and ARTCC-wide center NOTAMs. We already pull all
                   active TFRs into /api/tfr — once the airport-aware filter lands, this tab will
                   surface only the ones intersecting a configurable radius.`}
          />
        )}
      </div>
    </div>
  );
}

function Stub({ label, source, body }: { label: string; source: string; body: string }) {
  return (
    <div className="space-y-2">
      <div className="text-[10px] tracking-widest text-cockpit-400">{label.toUpperCase()}</div>
      <div className="bg-cockpit-700/20 border border-dashed border-cockpit-600 rounded-md px-3 py-3 text-xs text-cockpit-300">
        <p className="leading-snug">Coming soon.</p>
        <p className="mt-2 leading-snug text-cockpit-300/90">{body}</p>
        <p className="mt-2 text-[10px] text-cockpit-400">Source: {source}</p>
      </div>
    </div>
  );
}
